import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { limparDocumento, mascaraDocumento } from '@/lib/mascaras';
import DashboardLayout from '@/components/feature/DashboardLayout';
import LiberacoesDeAcesso from './LiberacoesDeAcesso';
import {
  fmtCentavos,
  fmtData,
  statusCliente,
  statusAssinatura,
  statusDoCliente,
} from '@/pages/admin/shared';

type TabKey = 'dados' | 'condominios' | 'historico' | 'liberacoes';

// ─── Retorno de `cliente_da_plataforma()` (migration 0026) ──────────

interface FaturaDoCliente {
  id: string;
  numero: string | null;
  status: string;
  situacao: 'pago' | 'pendente' | 'atrasado' | 'cancelado';
  metodo_pagamento: string | null;
  valor: number;
  moeda: string;
  vencimento: string | null;
  pago_em: string | null;
  url_fatura: string | null;
  url_pdf: string | null;
  criado_em: string;
}

interface ResumoFaturas {
  total: number;
  pago: number;
  em_aberto: number;
  atrasado: number;
}

interface AssinaturaDoCliente {
  status: string;
  metodo_pagamento: string | null;
  periodo_inicio: string | null;
  periodo_fim: string | null;
  cancelamento_agendado: boolean;
  cancelada_em: string | null;
  criado_em: string;
}

interface RetornoDoCliente {
  cliente: {
    id: string;
    account_id: string | null;
    tipo: 'pf' | 'pj';
    nome: string | null;
    razao_social: string | null;
    nome_responsavel: string | null;
    cpf: string | null;
    cnpj: string | null;
    email: string | null;
    telefone: string | null;
    cep: string | null;
    logradouro: string | null;
    numero: string | null;
    complemento: string | null;
    bairro: string | null;
    cidade: string | null;
    estado: string | null;
    criado_em: string | null;
  };
  plano: { id: string; nome: string; preco: number; visibilidade: string } | null;
  assinatura: AssinaturaDoCliente | null;
  condominios: CondominioInfo[];
  usuarios: number;
  faturas: FaturaDoCliente[];
  resumo_faturas: ResumoFaturas;
}

interface CondominioInfo {
  id: string;
  nome: string;
  localizacao: string;
  blocos: number;
  unidades: number;
  ativo: boolean;
  endereco: {
    cep?: string;
    logradouro?: string;
    numero?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
  };
}

interface ClienteDetail {
  id: string;
  nome: string;
  responsavel: string;
  email: string;
  telefone: string;
  tipo: 'pf' | 'pj';
  documento: string;
  cpf: string | null;
  cnpj: string | null;
  razaoSocial: string | null;
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
  plano: string;
  planoId: string | null;
  planoInterno: boolean;
  /** Em centavos, como `planos.preco`. */
  valorMensal: number;
  status: 'ativo' | 'inativo' | 'cancelado';
  dataInicio: string;
  assinatura: AssinaturaDoCliente | null;
  accountId: string | null;
}

// O método vem de `faturas.metodo_pagamento`, no vocabulário que o webhook
// grava (`cartao` | `boleto` | `pix`), e não no do mock (`cartao_credito`).
function metodoIcon(m: string | null) {
  if (m === 'cartao') return 'ri-bank-card-line';
  if (m === 'pix') return 'ri-exchange-funds-line';
  if (m === 'boleto') return 'ri-barcode-line';
  return 'ri-question-line';
}

function metodoLabel(m: string | null) {
  if (m === 'cartao') return 'Cartão';
  if (m === 'pix') return 'PIX';
  if (m === 'boleto') return 'Boleto';
  return '—';
}

function statusPgColor(s: string) {
  if (s === 'pago') return 'bg-green-50 text-green-700 border-green-200';
  if (s === 'pendente') return 'bg-accent-50 text-accent-700 border-accent-200';
  if (s === 'atrasado') return 'bg-red-50 text-red-700 border-red-200';
  return 'bg-secondary-100 text-secondary-900 border-secondary-200';
}

function statusPgLabel(s: string) {
  if (s === 'pago') return 'Pago';
  if (s === 'pendente') return 'Pendente';
  if (s === 'atrasado') return 'Atrasado';
  return 'Cancelada';
}

function HeaderCliente({
  cliente,
  editando,
  onToggleEdit,
}: {
  cliente: ClienteDetail;
  editando: boolean;
  onToggleEdit: () => void;
}) {
  const navigate = useNavigate();
  const st = statusCliente[cliente.status] || statusCliente.inativo;

  const iniciais = cliente.nome
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('');

  return (
    <div className="mb-6">
      <button
        onClick={() => navigate('/admin/clientes')}
        className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors mb-3"
      >
        <i className="ri-arrow-left-line"></i>
        <span>Voltar para Clientes</span>
      </button>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
            <span className="text-base font-bold text-primary-700">{iniciais}</span>
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl md:text-2xl font-bold text-foreground-900">{cliente.nome}</h1>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${st.cor}`}>
                {st.label}
              </span>
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-900 border border-secondary-200">
                {cliente.tipo === 'pj' ? 'Pessoa Jurídica' : 'Pessoa Física'}
              </span>
            </div>
            <p className="text-sm text-foreground-500">
              Cliente desde {fmtData(cliente.dataInicio)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onToggleEdit}
            className={`whitespace-nowrap inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
              editando
                ? 'bg-foreground-900 text-background-50 hover:bg-foreground-800'
                : 'bg-primary-500 text-background-50 hover:bg-primary-600'
            }`}
          >
            <i className={`${editando ? 'ri-close-line' : 'ri-edit-line'} text-sm`}></i>
            {editando ? 'Cancelar' : 'Editar'}
          </button>
          {!editando && (
            <>
              <button className="whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium border border-background-300/60 bg-background-50 text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer">
                <i className="ri-mail-send-line text-sm"></i>
                Enviar e-mail
              </button>
              <button className="whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium border border-background-300/60 bg-background-50 text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer">
                <i className="ri-download-line text-sm"></i>
                Exportar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function CardsResumo({
  cliente,
  totalCondominios,
  totalUsuarios,
}: {
  cliente: ClienteDetail;
  totalCondominios: number;
  totalUsuarios: number;
}) {
  const assinatura = cliente.assinatura;
  const st = assinatura ? statusAssinatura[assinatura.status] : null;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
            <i className="ri-vip-crown-line text-primary-600 text-sm"></i>
          </div>
          <p className="text-xs font-medium text-foreground-500">Plano</p>
        </div>
        <p className="text-base font-bold text-foreground-900">
          {cliente.plano}
          {cliente.planoInterno && (
            <span className="ml-1.5 align-middle text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-900">
              Interno
            </span>
          )}
        </p>
        <p className="text-xs text-foreground-500">{fmtCentavos(cliente.valorMensal)}/mês</p>
      </div>
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
            <i className="ri-building-line text-green-600 text-sm"></i>
          </div>
          <p className="text-xs font-medium text-foreground-500">Condomínios</p>
        </div>
        <p className="text-2xl font-bold text-foreground-900">{totalCondominios}</p>
      </div>
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-8 h-8 rounded-lg bg-accent-50 flex items-center justify-center">
            <i className="ri-user-line text-accent-600 text-sm"></i>
          </div>
          <p className="text-xs font-medium text-foreground-500">Usuários</p>
        </div>
        <p className="text-2xl font-bold text-foreground-900">{totalUsuarios}</p>
      </div>
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center">
            <i className="ri-calendar-check-line text-secondary-900 text-sm"></i>
          </div>
          <p className="text-xs font-medium text-foreground-500">Assinatura</p>
        </div>
        <p className="text-base font-bold text-foreground-900">{st?.label || 'Sem assinatura'}</p>
        {assinatura?.periodo_fim && (
          <p className="text-xs text-foreground-500">
            {assinatura.cancelamento_agendado ? 'Encerra em ' : 'Renova em '}
            {fmtData(assinatura.periodo_fim)}
          </p>
        )}
      </div>
    </div>
  );
}

function TabDadosGerais({
  cliente,
  editando,
  formState,
  onFormChange,
  onSave,
  saving,
  saveError,
  saveSuccess,
}: {
  cliente: ClienteDetail;
  editando: boolean;
  formState: ClienteDetail;
  onFormChange: (field: string, value: string) => void;
  onSave: () => void;
  saving: boolean;
  saveError: string | null;
  saveSuccess: boolean;
}) {
  const inputClass =
    'w-full px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400';

  const currentData = editando ? formState : cliente;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Dados do Cliente */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-foreground-900 mb-4 flex items-center gap-2">
          <i className="ri-user-settings-line text-foreground-500"></i>
          Dados do Cliente
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-foreground-500 mb-0.5">Tipo</p>
            {editando ? (
              <select
                value={formState.tipo}
                onChange={(e) => onFormChange('tipo', e.target.value)}
                className={inputClass}
              >
                <option value="pf">Pessoa Física (PF)</option>
                <option value="pj">Pessoa Jurídica (PJ)</option>
              </select>
            ) : (
              <p className="text-sm font-medium text-foreground-800">
                {currentData.tipo === 'pj' ? 'Pessoa Jurídica (PJ)' : 'Pessoa Física (PF)'}
              </p>
            )}
          </div>
          <div>
            <p className="text-xs text-foreground-500 mb-0.5">
              {currentData.tipo === 'pj' ? 'CNPJ' : 'CPF'}
            </p>
            {editando ? (
              <input
                type="text"
                inputMode={currentData.tipo === 'pj' ? undefined : 'numeric'}
                autoCapitalize={currentData.tipo === 'pj' ? 'characters' : undefined}
                value={mascaraDocumento(
                  (currentData.tipo === 'pj' ? formState.cnpj : formState.cpf) || '',
                  currentData.tipo === 'pj' ? 'pj' : 'pf',
                )}
                onChange={(e) =>
                  onFormChange(
                    currentData.tipo === 'pj' ? 'cnpj' : 'cpf',
                    limparDocumento(e.target.value, currentData.tipo === 'pj' ? 'pj' : 'pf'),
                  )
                }
                className={inputClass}
              />
            ) : (
              <p className="text-sm font-medium text-foreground-800">{currentData.documento}</p>
            )}
          </div>
          {currentData.tipo === 'pj' ? (
            <>
              <div className="sm:col-span-2">
                <p className="text-xs text-foreground-500 mb-0.5">Razão Social</p>
                {editando ? (
                  <input
                    type="text"
                    value={formState.razaoSocial || ''}
                    onChange={(e) => onFormChange('razaoSocial', e.target.value)}
                    className={inputClass}
                  />
                ) : (
                  <p className="text-sm font-medium text-foreground-800">
                    {currentData.razaoSocial || currentData.nome}
                  </p>
                )}
              </div>
              <div className="sm:col-span-2">
                <p className="text-xs text-foreground-500 mb-0.5">Nome Fantasia</p>
                {editando ? (
                  <input
                    type="text"
                    value={formState.nome}
                    onChange={(e) => onFormChange('nome', e.target.value)}
                    className={inputClass}
                  />
                ) : (
                  <p className="text-sm font-medium text-foreground-800">{currentData.nome}</p>
                )}
              </div>
            </>
          ) : (
            <div className="sm:col-span-2">
              <p className="text-xs text-foreground-500 mb-0.5">Nome Completo</p>
              {editando ? (
                <input
                  type="text"
                  value={formState.nome}
                  onChange={(e) => onFormChange('nome', e.target.value)}
                  className={inputClass}
                />
              ) : (
                <p className="text-sm font-medium text-foreground-800">{currentData.nome}</p>
              )}
            </div>
          )}
          <div>
            <p className="text-xs text-foreground-500 mb-0.5">Responsável</p>
            {editando ? (
              <input
                type="text"
                value={formState.responsavel}
                onChange={(e) => onFormChange('responsavel', e.target.value)}
                className={inputClass}
              />
            ) : (
              <p className="text-sm font-medium text-foreground-800">{currentData.responsavel}</p>
            )}
          </div>
          <div>
            <p className="text-xs text-foreground-500 mb-0.5">Status</p>
            <span
              className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${statusCliente[currentData.status].cor}`}
            >
              {statusCliente[currentData.status].label}
            </span>
          </div>
        </div>
      </div>

      {/* Contato */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-foreground-900 mb-4 flex items-center gap-2">
          <i className="ri-contacts-line text-foreground-500"></i>
          Contato
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-foreground-500 mb-0.5">E-mail</p>
            {editando ? (
              <input
                type="email"
                value={formState.email}
                onChange={(e) => onFormChange('email', e.target.value)}
                className={inputClass}
              />
            ) : (
              <p className="text-sm font-medium text-foreground-800 break-all">
                {currentData.email}
              </p>
            )}
          </div>
          <div>
            <p className="text-xs text-foreground-500 mb-0.5">Telefone</p>
            {editando ? (
              <input
                type="text"
                value={formState.telefone}
                onChange={(e) => onFormChange('telefone', e.target.value)}
                className={inputClass}
              />
            ) : (
              <p className="text-sm font-medium text-foreground-800">{currentData.telefone}</p>
            )}
          </div>
        </div>
      </div>

      {/* Endereço */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 lg:col-span-2">
        <h3 className="text-sm font-semibold text-foreground-900 mb-4 flex items-center gap-2">
          <i className="ri-map-pin-line text-foreground-500"></i>
          Endereço
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <div>
            <p className="text-xs text-foreground-500 mb-0.5">CEP</p>
            {editando ? (
              <input
                type="text"
                value={formState.cep}
                onChange={(e) => onFormChange('cep', e.target.value)}
                className={inputClass}
              />
            ) : (
              <p className="text-sm font-medium text-foreground-800">{currentData.cep}</p>
            )}
          </div>
          <div className="sm:col-span-2 lg:col-span-2">
            <p className="text-xs text-foreground-500 mb-0.5">Logradouro</p>
            {editando ? (
              <input
                type="text"
                value={formState.logradouro}
                onChange={(e) => onFormChange('logradouro', e.target.value)}
                className={inputClass}
              />
            ) : (
              <p className="text-sm font-medium text-foreground-800">{currentData.logradouro}</p>
            )}
          </div>
          <div>
            <p className="text-xs text-foreground-500 mb-0.5">Número</p>
            {editando ? (
              <input
                type="text"
                value={formState.numero}
                onChange={(e) => onFormChange('numero', e.target.value)}
                className={inputClass}
              />
            ) : (
              <p className="text-sm font-medium text-foreground-800">{currentData.numero}</p>
            )}
          </div>
          <div>
            <p className="text-xs text-foreground-500 mb-0.5">Complemento</p>
            {editando ? (
              <input
                type="text"
                value={formState.complemento}
                onChange={(e) => onFormChange('complemento', e.target.value)}
                className={inputClass}
              />
            ) : (
              <p className="text-sm font-medium text-foreground-800">
                {currentData.complemento || '—'}
              </p>
            )}
          </div>
          <div>
            <p className="text-xs text-foreground-500 mb-0.5">Bairro</p>
            {editando ? (
              <input
                type="text"
                value={formState.bairro}
                onChange={(e) => onFormChange('bairro', e.target.value)}
                className={inputClass}
              />
            ) : (
              <p className="text-sm font-medium text-foreground-800">{currentData.bairro}</p>
            )}
          </div>
          <div>
            <p className="text-xs text-foreground-500 mb-0.5">Cidade</p>
            {editando ? (
              <input
                type="text"
                value={formState.cidade}
                onChange={(e) => onFormChange('cidade', e.target.value)}
                className={inputClass}
              />
            ) : (
              <p className="text-sm font-medium text-foreground-800">{currentData.cidade}</p>
            )}
          </div>
          <div>
            <p className="text-xs text-foreground-500 mb-0.5">Estado</p>
            {editando ? (
              <input
                type="text"
                value={formState.estado}
                onChange={(e) => onFormChange('estado', e.target.value)}
                className={inputClass}
              />
            ) : (
              <p className="text-sm font-medium text-foreground-800">{currentData.estado}</p>
            )}
          </div>
        </div>
      </div>

      {/* Botões de salvar */}
      {editando && (
        <div className="lg:col-span-2 flex items-center gap-3">
          <button
            onClick={onSave}
            disabled={saving}
            className="whitespace-nowrap inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin text-sm"></i>
                Salvando...
              </>
            ) : (
              <>
                <i className="ri-check-line text-sm"></i>
                Salvar alterações
              </>
            )}
          </button>
          {saveError && (
            <span className="text-sm text-red-600 flex items-center gap-1">
              <i className="ri-error-warning-line"></i>
              {saveError}
            </span>
          )}
          {saveSuccess && (
            <span className="text-sm text-green-600 flex items-center gap-1">
              <i className="ri-check-line"></i>
              Alterações salvas com sucesso!
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function TabCondominios({ condominios }: { condominios: CondominioInfo[] }) {
  if (condominios.length === 0) {
    return (
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-10 text-center">
        <div className="w-12 h-12 rounded-full bg-secondary-100 flex items-center justify-center mx-auto mb-3">
          <i className="ri-building-2-line text-secondary-900 text-lg"></i>
        </div>
        <p className="text-sm font-medium text-foreground-800 mb-1">Nenhum condomínio vinculado</p>
        <p className="text-xs text-foreground-500">
          Este cliente ainda não gerencia nenhum condomínio.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {condominios.map((cond) => (
        <div
          key={cond.id}
          className="bg-background-50 border border-background-200/70 rounded-lg p-4 flex flex-col gap-3"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-building-line text-green-600 text-sm"></i>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground-900 truncate">{cond.nome}</p>
                <p className="text-xs text-foreground-500 truncate">{cond.localizacao}</p>
              </div>
            </div>
            <span
              className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border flex-shrink-0 ${
                cond.ativo
                  ? 'bg-green-50 text-green-700 border-green-200'
                  : 'bg-red-50 text-red-700 border-red-200'
              }`}
            >
              {cond.ativo ? 'Ativo' : 'Inativo'}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-background-200/50">
            <div>
              <p className="text-xs text-foreground-500">Blocos</p>
              <p className="text-sm font-semibold text-foreground-800">{cond.blocos}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-500">Unidades</p>
              <p className="text-sm font-semibold text-foreground-800">{cond.unidades}</p>
            </div>
          </div>
          {cond.endereco?.logradouro && (
            <div className="pt-1 border-t border-background-200/50">
              <p className="text-xs text-foreground-500 truncate">
                {cond.endereco.logradouro}, {cond.endereco.numero || 'S/N'}
                {cond.endereco.bairro ? ` - ${cond.endereco.bairro}` : ''}
              </p>
              <p className="text-xs text-foreground-400 truncate">
                {cond.endereco.cidade} / {cond.endereco.estado}
                {cond.endereco.cep ? ` - ${cond.endereco.cep}` : ''}
              </p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function TabHistorico({ faturas, resumo }: { faturas: FaturaDoCliente[]; resumo: ResumoFaturas }) {
  if (faturas.length === 0) {
    return (
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-10 text-center">
        <div className="w-12 h-12 rounded-full bg-secondary-100 flex items-center justify-center mx-auto mb-3">
          <i className="ri-history-line text-secondary-900 text-lg"></i>
        </div>
        <p className="text-sm font-medium text-foreground-800 mb-1">Nenhuma fatura registrada</p>
        <p className="text-xs text-foreground-500">
          As faturas aparecem aqui conforme o Stripe as emite — o webhook espelha cada uma na tabela{' '}
          <code>faturas</code>.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Cards financeiros */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <p className="text-xs text-foreground-500 mb-1">Total de faturas</p>
          <p className="text-2xl font-bold text-foreground-900">{resumo.total}</p>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <p className="text-xs text-foreground-500 mb-1">Total pago</p>
          <p className="text-2xl font-bold text-green-700">{fmtCentavos(resumo.pago)}</p>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <p className="text-xs text-foreground-500 mb-1">Pendente / Atrasado</p>
          <p className="text-2xl font-bold text-red-700">{fmtCentavos(resumo.em_aberto)}</p>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <p className="text-xs text-foreground-500 mb-1">Total transacionado</p>
          <p className="text-2xl font-bold text-foreground-900">
            {fmtCentavos(resumo.pago + resumo.em_aberto)}
          </p>
        </div>
      </div>

      {/* Tabela de histórico */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-background-200/70 bg-background-100/50">
                <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                  Fatura
                </th>
                <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                  Vencimento
                </th>
                <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                  Pagamento
                </th>
                <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap hidden sm:table-cell">
                  Método
                </th>
                <th className="text-right text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                  Valor
                </th>
                <th className="text-center text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                  Status
                </th>
                <th className="text-right text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200/50">
              {faturas.map((fat) => (
                <tr key={fat.id} className="hover:bg-background-100/30 transition-colors">
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono text-foreground-600">
                      {fat.numero || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-foreground-700 whitespace-nowrap">
                      {fmtData(fat.vencimento)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-foreground-700 whitespace-nowrap">
                      {fmtData(fat.pago_em)}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <div className="inline-flex items-center gap-1.5">
                      <div className="w-6 h-6 rounded flex items-center justify-center bg-secondary-50">
                        <i
                          className={`${metodoIcon(fat.metodo_pagamento)} text-secondary-700 text-xs`}
                        ></i>
                      </div>
                      <span className="text-sm text-foreground-600">
                        {metodoLabel(fat.metodo_pagamento)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm font-semibold text-foreground-800 whitespace-nowrap">
                      {fmtCentavos(fat.valor)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusPgColor(fat.situacao)}`}
                    >
                      {statusPgLabel(fat.situacao)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {fat.url_pdf || fat.url_fatura ? (
                      <a
                        href={(fat.url_pdf || fat.url_fatura) as string}
                        target="_blank"
                        rel="noreferrer"
                        title="Abrir fatura no Stripe"
                        className="w-8 h-8 rounded-md inline-flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-primary-600 transition-colors"
                      >
                        <i className="ri-external-link-line text-sm"></i>
                      </a>
                    ) : (
                      <span className="text-sm text-foreground-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function AdminClienteDetailPage() {
  const { isSuperuser } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('dados');
  const [editando, setEditando] = useState(false);
  const [cliente, setCliente] = useState<ClienteDetail | null>(null);
  const [condominios, setCondominios] = useState<CondominioInfo[]>([]);
  const [usuarios, setUsuarios] = useState(0);
  const [faturas, setFaturas] = useState<FaturaDoCliente[]>([]);
  const [resumoFaturas, setResumoFaturas] = useState<ResumoFaturas | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [formState, setFormState] = useState<ClienteDetail | null>(null);

  // Reset save status when toggling edit mode
  const handleToggleEdit = useCallback(() => {
    if (editando) {
      setEditando(false);
      setSaveError(null);
      setSaveSuccess(false);
      if (cliente) setFormState({ ...cliente });
    } else {
      setEditando(true);
      setSaveError(null);
      setSaveSuccess(false);
      if (cliente) setFormState({ ...cliente });
    }
  }, [editando, cliente]);

  const handleFormChange = useCallback((field: string, value: string) => {
    setFormState((prev) => {
      if (!prev) return prev;
      const next = { ...prev, [field]: value };

      // Auto-update documento when tipo changes
      if (field === 'tipo') {
        if (value === 'pj') {
          next.documento = next.cnpj || '';
        } else {
          next.documento = next.cpf || '';
        }
      }
      if (field === 'cpf') {
        next.documento = value;
      }
      if (field === 'cnpj') {
        next.documento = value;
      }

      return next;
    });
    setSaveSuccess(false);
  }, []);

  const handleSave = useCallback(async () => {
    if (!formState || !cliente) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const updateData: Record<string, any> = {
        nome: formState.tipo === 'pf' ? formState.nome : undefined,
        razao_social: formState.tipo === 'pj' ? formState.razaoSocial || formState.nome : undefined,
        nome_responsavel: formState.responsavel,
        email: formState.email,
        telefone: formState.telefone,
        cep: formState.cep,
        logradouro: formState.logradouro,
        numero: formState.numero,
        complemento: formState.complemento,
        bairro: formState.bairro,
        cidade: formState.cidade,
        estado: formState.estado,
        tipo: formState.tipo,
        updated_at: new Date().toISOString(),
      };

      if (formState.tipo === 'pf') {
        updateData.cpf = formState.cpf;
        updateData.cnpj = null;
        updateData.razao_social = null;
      } else {
        updateData.cnpj = formState.cnpj;
        updateData.cpf = null;
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', cliente.id);

      if (updateError) throw updateError;

      // Update local state
      setCliente(formState);
      setEditando(false);
      setSaveSuccess(true);

      // Auto-hide success message
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      setSaveError(err.message || 'Erro ao salvar alterações');
    } finally {
      setSaving(false);
    }
  }, [formState, cliente]);

  // Uma chamada, e não sete: `cliente_da_plataforma()` (migration 0026) devolve
  // cadastro, plano, assinatura, condomínios, equipe e faturas de uma vez, com
  // a RLS de quem consulta. Não há fallback para mock — cliente que não existe
  // é cliente que não existe.
  const fetchCliente = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcError } = await supabase.rpc('cliente_da_plataforma', {
        p_profile_id: id,
      });

      if (rpcError) throw rpcError;

      const retorno = data as RetornoDoCliente | null;
      if (!retorno) {
        setCliente(null);
        setFormState(null);
        setCondominios([]);
        setFaturas([]);
        setResumoFaturas(null);
        setUsuarios(0);
        return;
      }

      const c = retorno.cliente;
      const detail: ClienteDetail = {
        id: c.id,
        nome: (c.tipo === 'pj' ? c.razao_social || c.nome : c.nome) || '',
        responsavel: c.nome_responsavel || c.nome || '',
        email: c.email || '',
        telefone: c.telefone || '',
        tipo: c.tipo || 'pf',
        documento: (c.tipo === 'pj' ? c.cnpj : c.cpf) || '',
        cpf: c.cpf,
        cnpj: c.cnpj,
        razaoSocial: c.razao_social,
        cep: c.cep || '',
        logradouro: c.logradouro || '',
        numero: c.numero || '',
        complemento: c.complemento || '',
        bairro: c.bairro || '',
        cidade: c.cidade || '',
        estado: c.estado || '',
        plano: retorno.plano?.nome || 'Sem plano',
        planoId: retorno.plano?.id ?? null,
        planoInterno: retorno.plano?.visibilidade === 'interno',
        valorMensal: retorno.plano?.preco ?? 0,
        status: statusDoCliente(retorno.assinatura?.status),
        dataInicio: c.criado_em?.slice(0, 10) || '',
        assinatura: retorno.assinatura,
        accountId: c.account_id,
      };

      setCliente(detail);
      setFormState({ ...detail });
      setCondominios(retorno.condominios || []);
      setUsuarios(retorno.usuarios || 0);
      setFaturas(retorno.faturas || []);
      setResumoFaturas(retorno.resumo_faturas);
    } catch (err: any) {
      console.error('Erro ao carregar cliente:', err);
      setError(err.message || 'Erro ao carregar o cliente');
      setCliente(null);
      setFormState(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!isSuperuser || !id) {
      setLoading(false);
      return;
    }
    void fetchCliente();
  }, [isSuperuser, id, fetchCliente]);

  if (!isSuperuser) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl flex flex-col items-center justify-center py-20">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
            <i className="ri-lock-line text-red-600 text-2xl"></i>
          </div>
          <h2 className="text-lg font-bold text-foreground-900 mb-2">Acesso Restrito</h2>
          <p className="text-sm text-foreground-500 text-center max-w-md">
            Esta área é reservada para o superusuário administrador da plataforma.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl flex items-center justify-center py-20">
          <div className="flex items-center gap-3 text-foreground-500">
            <i className="ri-loader-4-line animate-spin text-xl"></i>
            <span className="text-sm">Carregando dados do cliente...</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!cliente) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl flex flex-col items-center justify-center py-20">
          <div className="w-16 h-16 rounded-full bg-secondary-100 flex items-center justify-center mb-4">
            <i className="ri-user-search-line text-secondary-900 text-2xl"></i>
          </div>
          <h2 className="text-lg font-bold text-foreground-900 mb-2">Cliente não encontrado</h2>
          <p className="text-sm text-foreground-500 mb-4">
            O cliente que você procura não existe ou foi removido.
          </p>
          <button
            onClick={() => navigate('/admin/clientes')}
            className="whitespace-nowrap inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Voltar para Clientes
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl">
        <HeaderCliente cliente={cliente} editando={editando} onToggleEdit={handleToggleEdit} />
        <CardsResumo
          cliente={cliente}
          totalCondominios={condominios.length}
          totalUsuarios={usuarios}
        />

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-5 bg-background-100 rounded-full p-1 w-fit overflow-x-auto">
          {(
            [
              { key: 'dados', label: 'Dados Gerais', icon: 'ri-information-line' },
              { key: 'condominios', label: 'Condomínios Vinculados', icon: 'ri-building-line' },
              { key: 'historico', label: 'Histórico de Pagamentos', icon: 'ri-history-line' },
              { key: 'liberacoes', label: 'Liberações de Acesso', icon: 'ri-lock-unlock-line' },
            ] as const
          ).map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                if (editando) handleToggleEdit();
              }}
              className={`whitespace-nowrap inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${activeTab === tab.key ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
            >
              <i className={`${tab.icon} text-sm`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'dados' && formState && (
          <TabDadosGerais
            cliente={cliente}
            editando={editando}
            formState={formState}
            onFormChange={handleFormChange}
            onSave={handleSave}
            saving={saving}
            saveError={saveError}
            saveSuccess={saveSuccess}
          />
        )}
        {activeTab === 'condominios' && <TabCondominios condominios={condominios} />}
        {activeTab === 'historico' && (
          <TabHistorico
            faturas={faturas}
            resumo={resumoFaturas || { total: 0, pago: 0, em_aberto: 0, atrasado: 0 }}
          />
        )}
        {activeTab === 'liberacoes' && (
          <LiberacoesDeAcesso
            profileId={cliente.id}
            accountId={cliente.accountId}
            assinaturaAtiva={['ativa', 'trialing'].includes(cliente.assinatura?.status ?? '')}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
